import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getAuthHeaders, WORKER_URL } from '../../utils/api';
import { ExternalLink, Pause, Play, Search, RefreshCw, ChevronDown } from 'lucide-react';

const STATUS_LABELS = {
    active: { label: 'Ativo', color: '#00a650' },
    paused: { label: 'Pausado', color: '#ff8000' },
    closed: { label: 'Encerrado', color: '#ff4444' },
    under_review: { label: 'Em revisão', color: '#999' },
    inactive: { label: 'Inativo', color: '#999' },
    not_yet_active: { label: 'Não ativado', color: '#999' },
    pending: { label: 'Pendente', color: '#ff8000' },
    programmed: { label: 'Agendado', color: '#2D3277' }
};

const ALL_STATUSES = ['active', 'paused', 'closed', 'under_review', 'inactive', 'not_yet_active', 'pending', 'programmed'];

const th = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#2D3277', fontSize: '0.8rem' };
const td = { padding: '10px 12px', verticalAlign: 'middle' };
const btnSm = (bg) => ({
    backgroundColor: bg, color: 'white', border: 'none', borderRadius: '4px',
    padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center'
});
const selectStyle = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.8rem', width: '100%' };
const inputStyle = { padding: '4px 6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.8rem', width: '100%' };

// ThFilter fora do componente pai para evitar remount a cada render
const ThFilter = ({ label, col, hasFilter, openDropdown, setOpenDropdown, onClear, children }) => {
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [setOpenDropdown]);

    return (
        <th style={{ ...th, position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {label}
                <button
                    onClick={() => setOpenDropdown(openDropdown === col ? null : col)}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                        color: hasFilter ? '#00a650' : '#2D3277', display: 'flex', alignItems: 'center'
                    }}
                >
                    <ChevronDown size={13} />
                </button>
            </div>
            {openDropdown === col && (
                <div ref={dropdownRef} style={{
                    position: 'absolute', top: '100%', left: 0, zIndex: 100,
                    backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '6px',
                    padding: '10px', minWidth: '180px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}>
                    {children}
                    {hasFilter && (
                        <button onClick={onClear} style={{ ...btnSm('#ccc'), justifyContent: 'center', fontSize: '0.8rem', marginTop: '6px', width: '100%' }}>Limpar</button>
                    )}
                </div>
            )}
        </th>
    );
};

function Produtos() {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [editingField, setEditingField] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState('');
    const [restrictedItems, setRestrictedItems] = useState([]);
    const [countdown, setCountdown] = useState(null);
    const countdownRef = useRef(null);

    const [openDropdown, setOpenDropdown] = useState(null);
    const [priceOp, setPriceOp] = useState('gt');
    const [priceVal, setPriceVal] = useState('');
    const [stockOp, setStockOp] = useState('gt');
    const [stockVal, setStockVal] = useState('');
    const [statusFilter, setStatusFilter] = useState([]);
    const [colFilter, setColFilter] = useState({ price: null, stock: null });

    const fetchProducts = useCallback(async () => {
        setIsLoading(true);
        try {
            const fetchStatus = async (status) => {
                try {
                    const params = new URLSearchParams({ status, limit: 50 });
                    const res = await fetch(`${WORKER_URL}/api/products?${params}`, { headers: getAuthHeaders() });
                    const data = await res.json();
                    return data.items || [];
                } catch {
                    return [];
                }
            };
            const results = await Promise.all(ALL_STATUSES.map(fetchStatus));
            setItems(results.flat());
        } catch (e) {
            console.error('Erro ao buscar produtos:', e);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(fetchProducts, 400);
        return () => clearTimeout(timer);
    }, [fetchProducts]);

    const startCountdown = () => {
        if (countdownRef.current) clearInterval(countdownRef.current);
        setCountdown(15);
        countdownRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) { clearInterval(countdownRef.current); countdownRef.current = null; return 0; }
                return prev - 1;
            });
        }, 1000);
    };

    const handleToggleStatus = async (item) => {
        const newStatus = item.status === 'active' ? 'paused' : 'active';
        try {
            await fetch(`${WORKER_URL}/api/products/${item.id}`, {
                method: 'PUT',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            setItems(prev => prev.map(i => i.id === item.id ? { ...i, status: newStatus } : i));
            startCountdown();
        } catch (e) {
            console.error('Erro ao alterar status:', e);
        }
    };

    const handleSaveField = async (itemId, field) => {
        let value = editingValue.replace(',', '.');
        if (field === 'price') value = parseFloat(value);
        else if (field === 'available_quantity') value = parseInt(value);
        if (!value || value <= 0) return;

        setSavingId(itemId);
        setError('');
        try {
            const res = await fetch(`${WORKER_URL}/api/products/${itemId}`, {
                method: 'PUT',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ [field]: value })
            });
            if (!res.ok) {
                const data = await res.json();
                const msg = data.message || JSON.stringify(data);
                if (msg.includes('Cannot update item')) {
                    setRestrictedItems(prev => [...prev, itemId]);
                    setEditingField(null);
                } else {
                    setError(msg);
                }
                return;
            }
            await new Promise(r => setTimeout(r, 1500));
            await fetchProducts();
            setEditingField(null);
            setEditingValue('');
        } catch (e) {
            setError('Erro de conexão ao salvar.');
        } finally {
            setSavingId(null);
        }
    };

    const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

    const startEdit = (itemId, field, currentValue) => {
        setEditingField({ id: itemId, field });
        setEditingValue(String(currentValue));
        setError('');
    };

    const applyPriceFilter = () => {
        const v = parseFloat(priceVal.replace(',', '.'));
        if (!isNaN(v)) setColFilter(f => ({ ...f, price: { op: priceOp, val: v } }));
        setOpenDropdown(null);
    };

    const applyStockFilter = () => {
        if (stockOp === 'zero') {
            setColFilter(f => ({ ...f, stock: { op: 'zero' } }));
        } else {
            const v = parseInt(stockVal);
            if (!isNaN(v)) setColFilter(f => ({ ...f, stock: { op: stockOp, val: v } }));
        }
        setOpenDropdown(null);
    };

    const toggleStatusFilter = (s) => {
        setStatusFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
    };

    const visibleItems = items.filter(i => {
        if (search) {
            const q = search.toLowerCase();
            const sku = (i.seller_custom_field || i.attributes?.find(a => a.id === 'SELLER_SKU')?.value_name || '').toLowerCase();
            if (!i.title.toLowerCase().includes(q) && !sku.includes(q)) return false;
        }
        if (colFilter.price) {
            if (colFilter.price.op === 'gt' && i.price <= colFilter.price.val) return false;
            if (colFilter.price.op === 'lt' && i.price >= colFilter.price.val) return false;
        }
        if (colFilter.stock) {
            if (colFilter.stock.op === 'zero' && i.available_quantity !== 0) return false;
            if (colFilter.stock.op === 'gt' && i.available_quantity <= colFilter.stock.val) return false;
            if (colFilter.stock.op === 'lt' && i.available_quantity >= colFilter.stock.val) return false;
        }
        if (statusFilter.length > 0 && !statusFilter.includes(i.status)) return false;
        return true;
    });

    const InlineEdit = ({ item, field }) => {
        const isCatalogRestricted = !item.catalog_listing && !!item.catalog_product_id;
        if (isCatalogRestricted || restrictedItems.includes(item.id)) {
            return <span style={{ color: '#333' }}>{field === 'price' ? fmt(item.price) : item.available_quantity}</span>;
        }
        const isEditing = editingField?.id === item.id && editingField?.field === field;
        if (isEditing) return (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input
                    value={editingValue}
                    onChange={e => setEditingValue(e.target.value)}
                    style={{ width: '70px', padding: '3px 6px', border: '1px solid #2D3277', borderRadius: '4px', fontSize: '0.85rem' }}
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleSaveField(item.id, field); if (e.key === 'Escape') setEditingField(null); }}
                />
                <button onClick={() => handleSaveField(item.id, field)} disabled={savingId === item.id} style={btnSm('#00a650')}>
                    {savingId === item.id ? '...' : 'OK'}
                </button>
                <button onClick={() => setEditingField(null)} style={btnSm('#ccc')}>✕</button>
            </div>
        );
        return (
            <span onClick={() => startEdit(item.id, field, field === 'price' ? item.price : item.available_quantity)}
                style={{ cursor: 'pointer', textDecoration: 'underline dotted', color: field === 'available_quantity' && item.available_quantity === 0 ? '#ff4444' : '#2D3277' }}>
                {field === 'price' ? fmt(item.price) : item.available_quantity}
            </span>
        );
    };

    return (
        <div style={{ padding: '20px' }}>
            <h2 style={{ color: '#2D3277', marginBottom: '16px' }}>Produtos</h2>
            {error && <div style={{ backgroundColor: '#fff0f0', border: '1px solid #ffcccc', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px', color: '#cc0000', fontSize: '0.85rem' }}>{error}</div>}

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', flex: 1, maxWidth: '320px' }}>
                    <Search size={16} color="#999" />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Buscar produto..."
                        style={{ border: 'none', outline: 'none', width: '100%', fontSize: '0.875rem' }}
                    />
                </div>
                {countdown !== null && (
                    countdown > 0
                        ? <span style={{ color: '#999', fontSize: '0.8rem' }}>Atualizando em {countdown}s...</span>
                        : <button onClick={() => { fetchProducts(); setCountdown(null); }} style={{ ...btnSm('#2D3277'), gap: '6px', padding: '6px 14px', fontSize: '0.8rem' }}>
                            <RefreshCw size={14} /> Atualizar
                          </button>
                )}
                <span style={{ color: '#999', fontSize: '0.8rem', marginLeft: 'auto' }}>{visibleItems.length} anúncios</span>
            </div>

            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Carregando...</div>
            ) : items.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Nenhum produto encontrado</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                            <th style={th}>Produto</th>
                            <th style={th}>SKU</th>

                            <ThFilter col="price" label="Preço" hasFilter={!!colFilter.price} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} onClear={() => setColFilter(f => ({ ...f, price: null }))}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <select value={priceOp} onChange={e => setPriceOp(e.target.value)} style={selectStyle}>
                                        <option value="gt">Maior que</option>
                                        <option value="lt">Menor que</option>
                                    </select>
                                    <input
                                        type="number" value={priceVal}
                                        onChange={e => setPriceVal(e.target.value)}
                                        placeholder="Valor (R$)" style={inputStyle}
                                        onKeyDown={e => e.key === 'Enter' && applyPriceFilter()}
                                    />
                                    <button onClick={applyPriceFilter} style={{ ...btnSm('#2D3277'), justifyContent: 'center', fontSize: '0.8rem' }}>Aplicar</button>
                                </div>
                            </ThFilter>

                            <ThFilter col="stock" label="Estoque" hasFilter={!!colFilter.stock} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} onClear={() => setColFilter(f => ({ ...f, stock: null }))}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <select value={stockOp} onChange={e => setStockOp(e.target.value)} style={selectStyle}>
                                        <option value="gt">Maior que</option>
                                        <option value="lt">Menor que</option>
                                        <option value="zero">Igual a 0</option>
                                    </select>
                                    {stockOp !== 'zero' && (
                                        <input
                                            type="number" value={stockVal}
                                            onChange={e => setStockVal(e.target.value)}
                                            placeholder="Quantidade" style={inputStyle}
                                            onKeyDown={e => e.key === 'Enter' && applyStockFilter()}
                                        />
                                    )}
                                    <button onClick={applyStockFilter} style={{ ...btnSm('#2D3277'), justifyContent: 'center', fontSize: '0.8rem' }}>Aplicar</button>
                                </div>
                            </ThFilter>

                            <ThFilter col="status" label="Status" hasFilter={statusFilter.length > 0} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} onClear={() => setStatusFilter([])}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {ALL_STATUSES.map(s => (
                                        <label key={s} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.8rem' }}>
                                            <input type="checkbox" checked={statusFilter.includes(s)} onChange={() => toggleStatusFilter(s)} />
                                            <span style={{ color: STATUS_LABELS[s].color, fontWeight: 500 }}>{STATUS_LABELS[s].label}</span>
                                        </label>
                                    ))}
                                </div>
                            </ThFilter>

                            <th style={th}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visibleItems.map(item => (
                            <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={td}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <img src={item.thumbnail} alt={item.title} style={{ width: '48px', height: '48px', objectFit: 'contain', borderRadius: '4px' }} />
                                        <span style={{ fontWeight: 500 }}>{item.title}</span>
                                    </div>
                                </td>
                                <td style={{ ...td, color: '#666', fontSize: '0.8rem' }}>
                                    {item.seller_custom_field || item.attributes?.find(a => a.id === 'SELLER_SKU')?.value_name || <span style={{ color: '#ccc' }}>—</span>}
                                </td>
                                <td style={td}><InlineEdit item={item} field="price" /></td>
                                <td style={td}><InlineEdit item={item} field="available_quantity" /></td>
                                <td style={td}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <span style={{ color: STATUS_LABELS[item.status]?.color || '#999', fontWeight: 500 }}>
                                            {STATUS_LABELS[item.status]?.label || item.status}
                                        </span>
                                        {((!item.catalog_listing && !!item.catalog_product_id) || restrictedItems.includes(item.id)) && (
                                            <span style={{ fontSize: '0.7rem', backgroundColor: '#f0f0f0', color: '#666', padding: '1px 6px', borderRadius: '10px', width: 'fit-content' }}>
                                                Catálogo ML
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td style={td}>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button
                                            onClick={() => handleToggleStatus(item)}
                                            title={item.status === 'active' ? 'Pausar' : 'Reativar'}
                                            style={btnSm(item.status === 'active' ? '#ff8000' : '#00a650')}
                                        >
                                            {item.status === 'active' ? <Pause size={12} /> : <Play size={12} />}
                                        </button>
                                        <a href={item.permalink} target="_blank" rel="noopener noreferrer" title="Ver no ML" style={btnSm('#2D3277')}>
                                            <ExternalLink size={12} />
                                        </a>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

export default Produtos;
